import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Calendar, TrendingUp, AlertTriangle } from "lucide-react";
import { Subscription } from "@/types/subscription";

interface SubscriptionStatsProps {
  subscriptions: Subscription[];
}

export const SubscriptionStats = ({ subscriptions }: SubscriptionStatsProps) => {
  // Calculate monthly total (convert all to monthly)
  const monthlyTotal = subscriptions
    .filter(sub => sub.status === 'active')
    .reduce((total, sub) => {
      let monthlyAmount = sub.amount;
      if (sub.frequency === 'yearly') monthlyAmount = sub.amount / 12;
      if (sub.frequency === 'weekly') monthlyAmount = sub.amount * 4.33; // Average weeks per month
      return total + monthlyAmount;
    }, 0);

  const yearlyTotal = monthlyTotal * 12;

  // Find subscriptions due in next 7 days
  const upcomingDue = subscriptions.filter(sub => {
    if (sub.status !== 'active') return false;
    const nextBilling = new Date(sub.nextBilling);
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextBilling <= nextWeek && nextBilling >= new Date();
  });

  // Count active subscriptions
  const activeCount = subscriptions.filter(sub => sub.status === 'active').length;

  // Find most expensive subscription
  const mostExpensive = subscriptions
    .filter(sub => sub.status === 'active')
    .reduce((max, sub) => {
      let monthlyAmount = sub.amount;
      if (sub.frequency === 'yearly') monthlyAmount = sub.amount / 12;
      if (sub.frequency === 'weekly') monthlyAmount = sub.amount * 4.33;
      
      let maxMonthly = max.amount;
      if (max.frequency === 'yearly') maxMonthly = max.amount / 12;
      if (max.frequency === 'weekly') maxMonthly = max.amount * 4.33;
      
      return monthlyAmount > maxMonthly ? sub : max;
    }, subscriptions.find(sub => sub.status === 'active') || subscriptions[0]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Monthly Total</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">${monthlyTotal.toFixed(2)}</div>
          <p className="text-xs text-muted-foreground">
            ${yearlyTotal.toFixed(2)} per year
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activeCount}</div>
          <p className="text-xs text-muted-foreground">
            {subscriptions.length - activeCount} inactive
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Due This Week</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{upcomingDue.length}</div>
          <p className="text-xs text-muted-foreground">
            ${upcomingDue.reduce((sum, sub) => sum + sub.amount, 0).toFixed(2)} total
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Highest Subscription</CardTitle>
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            ${mostExpensive?.amount.toFixed(2) || '0.00'}
          </div>
          <p className="text-xs text-muted-foreground">
            {mostExpensive?.name || 'No active subscriptions'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};